import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Sale, SaleItem, ShippingAddress, ShippingService, PickupStore, CheckoutCustomerData, CheckoutSession, CheckoutPayment, getLatestCheckoutSession, useSaleMutations } from "@/hooks/useSales";
import { useShipments } from "@/hooks/useShipments";
import { useProducts } from "@/hooks/useProducts";
import { salesRepository } from "@/services";
import { useTenant } from "@/hooks/useTenant";
import { ShippingStatus } from "./ShippingStatus";
import { ShippingModal } from "./ShippingModal";
import { useSuperFreteCancel, useSuperFreteRetryPrint, useSuperFreteFetchLabel } from "@/hooks/useSuperFrete";
import { toast } from "@/hooks/use-toast";
import { CreateCheckoutButton } from "./CreateCheckoutButton";
import { CheckoutLinkDisplay } from "./CheckoutLinkDisplay";
import { useSaleHistory } from "@/hooks/useSaleHistory";
import { SaleTimeline } from "./SaleTimeline";
import {
  Mail,
  Phone,
  Calendar,
  Truck,
  Package,
  Store,
  Hash,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  Banknote,
  CreditCard,
  RotateCcw,
  Printer,
  MapPin,
  Bike,
} from "lucide-react";
import { printSale } from "./SalePrint";

interface SaleDetailsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sale: Sale | null;
  storeAddresses?: Array<{
    id?: string;
    name: string;
    phone: string;
    email: string;
    document: string;
    street: string;
    number: string;
    complement?: string;
    neighborhood: string;
    city: string;
    state: string;
    cep: string;
  }> | null;
}

const SALES_CHANNELS: Record<string, string> = {
  story_shop: "Story Shop",
  live: "Live",
  manual: "Manual",
};

const PAYMENT_METHODS: Record<string, string> = {
  pix: "Pix",
  card: "Cartão",
  checkout: "Checkout (Link)",
  cash: "Dinheiro",
  outro: "Outro",
};

const PAYMENT_STATUS: Record<string, { label: string; variant: "default" | "secondary" | "destructive"; className?: string; icon: React.ElementType }> = {
  pending: { label: "Pendente", variant: "secondary", icon: Clock },
  paid: { label: "Pago", variant: "default", icon: CheckCircle },
  cancelled: { label: "Cancelado", variant: "destructive", icon: XCircle },
  pay_on_pickup: { label: "Pgto na Retirada", variant: "secondary", className: "bg-amber-500 text-white border-amber-500", icon: Clock },
};

const asCustomerData = (value: CheckoutSession["customer_data"]): CheckoutCustomerData | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as CheckoutCustomerData;
};

const asShippingAddress = (value: CheckoutSession["shipping_address"]): ShippingAddress | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as ShippingAddress;
};

const asObjectRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
};

const readString = (value: unknown): string | null => {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
};

const normalizeAddressRecord = (value: unknown): ShippingAddress | null => {
  const record = asObjectRecord(value);
  if (!record) return null;

  const street = readString(record.street) || readString(record.address) || readString(record.logradouro);
  const number = readString(record.number) || readString(record.numero) || "";
  const neighborhood = readString(record.neighborhood) || readString(record.district) || readString(record.bairro);
  const city = readString(record.city) || readString(record.cidade);
  const state = readString(record.state) || readString(record.state_abbr) || readString(record.estado) || readString(record.uf);
  const cep = readString(record.cep) || readString(record.postal_code);
  const complement = readString(record.complement) || readString(record.complemento) || undefined;

  if (!street || !city || !state) return null;

  return {
    street,
    number,
    complement,
    neighborhood: neighborhood || "",
    city,
    state,
    cep: cep || "",
  };
};

const extractShipmentAddress = (metadata: unknown): ShippingAddress | null => {
  const root = asObjectRecord(metadata);
  if (!root) return null;

  const candidates = [
    root.to,
    asObjectRecord(root.cart)?.to,
    asObjectRecord(root.order)?.to,
    asObjectRecord(root.checkout)?.to,
    asObjectRecord(asObjectRecord(root.pre_postagem)?.destinatario)?.endereco,
    root.status,
    root.envio,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeAddressRecord(candidate);
    if (normalized) return normalized;
  }

  return null;
};

const safeNumber = (value: unknown, fallback = 0) => {
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

type CheckoutItem = NonNullable<NonNullable<CheckoutSession["items"]>[number]>;

const itemsFromCheckoutSession = (session?: CheckoutSession | null): SaleItem[] => {
  if (!session || !Array.isArray(session.items)) return [];
  const items = session.items.filter((i): i is CheckoutItem => !!i);
  if (items.length === 0) return [];
  return items.map((ci) => ({
    product_id: ci.product_id || null,
    variation_id: ci.variation_id || null,
    product_name: ci.name || "Produto",
    product_code: (ci as any).product_code || null,
    variation_description: ci.variation_description || null,
    quantity: safeNumber(ci.quantity, 1),
    unit_price: safeNumber(ci.unit_price, 0),
    total_price: safeNumber(ci.unit_price, 0) * safeNumber(ci.quantity, 1),
  }));
};

const compactAddress = (address: {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  cep?: string | null;
}) => {
  const line1 = [address.street, address.number].filter(Boolean).join(", ");
  const line1WithComplement = [line1, address.complement].filter(Boolean).join(" - ");
  const line2 = [address.neighborhood, [address.city, address.state].filter(Boolean).join("/")].filter(Boolean).join(" — ");
  return [line1WithComplement, line2, address.cep].filter(Boolean).join(" · ");
};

const getRelevantCheckoutPayment = (session?: CheckoutSession | null) => {
  const payments = session?.checkout_payments;
  if (!payments?.length) return null;

  return [...payments].sort((a, b) => {
    const aApproved = a.status === "approved" ? 1 : 0;
    const bApproved = b.status === "approved" ? 1 : 0;
    if (aApproved !== bApproved) return bApproved - aApproved;

    const aTime = new Date(a.paid_at || a.created_at || 0).getTime();
    const bTime = new Date(b.paid_at || b.created_at || 0).getTime();
    return bTime - aTime;
  })[0];
};

const getPaymentSummaryLabel = (sale: Sale, session?: CheckoutSession | null, payment?: CheckoutPayment | null) => {
  if (sale.payment_method !== "checkout") {
    return sale.payment_method ? PAYMENT_METHODS[sale.payment_method] : "—";
  }

  const paymentMethod = payment?.payment_method || session?.payment_method || null;
  const metadata = payment?.metadata && typeof payment.metadata === "object" && !Array.isArray(payment.metadata)
    ? payment.metadata as Record<string, unknown>
    : null;
  const installments = Number(metadata?.installments);

  if (paymentMethod) {
    const normalized = paymentMethod.toLowerCase();
    const isPix = normalized.includes("pix");
    const isCard = normalized.includes("credit") || normalized.includes("card");

    if (isPix) return "Checkout (Pix)";
    if (isCard) {
      if (Number.isFinite(installments) && installments > 1) {
        return `Checkout (Cartao em ${installments}x)`;
      }
      return "Checkout (Cartao)";
    }

    const fallbackMethod = PAYMENT_METHODS[normalized] || paymentMethod;
    return `Checkout (${fallbackMethod})`;
  }

  return "Checkout (Link)";
};

export function SaleDetails({ open, onOpenChange, sale: saleProp, storeAddresses }: SaleDetailsProps) {
  const [shippingModalOpen, setShippingModalOpen] = useState(false);
  const { tenant } = useTenant();
  const { data: fullSale, isLoading: isFullSaleLoading } = useQuery({
    queryKey: ["sale-detail", saleProp?.id],
    queryFn: async () => {
      const { data, error } = await salesRepository.getById(tenant!.id, saleProp!.id);
      if (error) throw error;
      return data as Sale;
    },
    enabled: !!saleProp?.id && open && !!tenant?.id,
    staleTime: 0,
  });
  const sale = fullSale || saleProp;
  const { data: shipments } = useShipments(sale?.id);
  const { products } = useProducts();
  const cancelShipmentMutation = useSuperFreteCancel();
  const retryPrintMutation = useSuperFreteRetryPrint();
  const fetchLabelMutation = useSuperFreteFetchLabel();
  const { events, isLoading: isHistoryLoading } = useSaleHistory(sale?.id);
  const { confirmPickupPayment } = useSaleMutations();

  if (!sale) return null;

  const formatCurrency = (value: number) => {
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncateId = (id: string) => {
    return id.slice(0, 8).toUpperCase();
  };

  const paymentStatus = sale.payment_status ? PAYMENT_STATUS[sale.payment_status] : null;
  const PaymentIcon = paymentStatus?.icon || Clock;
  const isDelivery = sale.delivery_method === "delivery";
  const latestShipment = shipments?.[0];

  const checkoutSession = getLatestCheckoutSession(sale.checkout_sessions);
  const checkoutPayment = getRelevantCheckoutPayment(checkoutSession);
  const allSessions = Array.isArray(sale.checkout_sessions) ? sale.checkout_sessions : [];
  const sessionCustomer = asCustomerData(checkoutSession?.customer_data)
    || allSessions.reduce<CheckoutCustomerData | null>(
      (found, s) => found || asCustomerData(s.customer_data),
      null,
    );
  const shippingAddress = asShippingAddress(checkoutSession?.shipping_address)
    || allSessions.reduce<ShippingAddress | null>(
      (found, s) => found || asShippingAddress(s.shipping_address),
      null,
    );
  const shipmentAddress = extractShipmentAddress(latestShipment?.metadata);
  const pickupStore = checkoutSession?.pickup_store as PickupStore | null | undefined;
  const customerAddress = {
    street: shippingAddress?.street || shipmentAddress?.street || sale.customer?.street || sessionCustomer?.street || null,
    number: shippingAddress?.number || shipmentAddress?.number || sale.customer?.number || sessionCustomer?.number || null,
    complement: shippingAddress?.complement || shipmentAddress?.complement || sale.customer?.complement || sessionCustomer?.complement || null,
    neighborhood: shippingAddress?.neighborhood || shipmentAddress?.neighborhood || sale.customer?.neighborhood || sessionCustomer?.neighborhood || null,
    city: shippingAddress?.city || shipmentAddress?.city || sale.customer?.city || sessionCustomer?.city || null,
    state: shippingAddress?.state || shipmentAddress?.state || sale.customer?.state || sessionCustomer?.state || null,
    cep: shippingAddress?.cep || shipmentAddress?.cep || sale.customer?.cep || sessionCustomer?.cep || null,
  };
  const displayCustomer = {
    name: sale.customer?.name || sessionCustomer?.name || (sessionCustomer?.instagram_username ? `@${sessionCustomer.instagram_username.replace(/^@/, "")}` : null),
    instagram: sale.customer?.instagram || sessionCustomer?.instagram || sessionCustomer?.instagram_username || null,
    email: sale.customer?.email || sessionCustomer?.email || null,
    whatsapp: sale.customer?.whatsapp || sessionCustomer?.whatsapp || null,
    document: sale.customer?.document || sessionCustomer?.document || null,
    ...customerAddress,
  };
  const hasCustomerAddress = Object.values(customerAddress).some(Boolean);
  const formattedCustomerAddress = hasCustomerAddress ? compactAddress(customerAddress) : null;
  const displayItems = (sale.sale_items && sale.sale_items.length > 0)
    ? sale.sale_items
    : itemsFromCheckoutSession(checkoutSession);
  const hasFullItemData = displayItems.some(item => item.quantity != null && item.unit_price != null);
  const showGenerateButton = !!fullSale &&
    sale.payment_status === "pending" &&
    hasFullItemData &&
    !checkoutSession;

  return (
    <>
      <ResponsiveModal open={open} onOpenChange={onOpenChange}>
        <ResponsiveModalContent fullscreen hideClose className="sm:max-w-[700px] sm:max-h-[85vh] sm:h-auto flex flex-col p-0 gap-0 overflow-hidden">
          <ResponsiveModalHeader className="px-4 sm:px-8 pt-4 sm:pt-8 pb-3 sm:pb-4 border-b shrink-0">
            <div className="flex items-center justify-between gap-2">
              <ResponsiveModalTitle className="text-lg sm:text-xl">Detalhes da Venda</ResponsiveModalTitle>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#EAEFFE] text-xs font-mono text-[#574BFB] shrink-0">
                <Hash className="h-3 w-3" />
                {truncateId(sale.id)}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-2.5 flex-wrap">
              {paymentStatus && (
                <Badge variant={paymentStatus.variant} className="gap-1.5">
                  <PaymentIcon className="h-3 w-3" />
                  {paymentStatus.label}
                </Badge>
              )}
              {sale.queue_origin && (
                <Badge variant="outline" className="gap-1 bg-[#EAEFFE] text-[#574BFB] border-[#C2DBF6]">
                  <RotateCcw className="h-3 w-3" />
                  Recuperado
                </Badge>
              )}
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                {formatDate(sale.created_at)}
              </span>
            </div>
          </ResponsiveModalHeader>

          <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-4 sm:py-6 space-y-4 sm:space-y-5">
            {isFullSaleLoading && !fullSale ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-[#574BFB]" />
              </div>
            ) : (<>
            {sale.payment_status === "pay_on_pickup" && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Banknote className="h-5 w-5 text-amber-600 shrink-0" />
                    <div>
                      <p className="text-sm font-medium">Aguardando pagamento na retirada</p>
                      <p className="text-xs text-muted-foreground">Confirme quando o cliente efetuar o pagamento</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => confirmPickupPayment.mutate(sale.id)}
                    disabled={confirmPickupPayment.isPending}
                  >
                    {confirmPickupPayment.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    Confirmar Pagamento
                  </Button>
                </div>
              </div>
            )}

            {(() => {
              const shippingSvc = checkoutSession?.shipping_service as ShippingService | null | undefined;
              const isPickup = sale.delivery_method === "pickup";
              const isLocalDelivery = sale.delivery_method === "delivery" && shippingSvc?.company === "Loja";
              const isCarrier = sale.delivery_method === "delivery" && !isLocalDelivery;

              const DeliveryIcon = isPickup ? Store : isLocalDelivery ? Bike : Truck;
              const deliveryLabel = isPickup
                ? "Retirada na Loja"
                : isLocalDelivery
                  ? "Entrega Local"
                  : "Transportadora";

              const paymentLabel = getPaymentSummaryLabel(sale, checkoutSession, checkoutPayment);

              return (
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#EAEFFE]/40 text-center">
                    <Store className="h-4 w-4 text-[#574BFB]" />
                    <div className="min-w-0 w-full">
                      <p className="text-[10px] text-muted-foreground">Canal</p>
                      <p className="text-xs font-medium truncate">
                        {sale.sales_channel ? SALES_CHANNELS[sale.sales_channel] : "—"}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#EAEFFE]/40 text-center">
                    <DeliveryIcon className="h-4 w-4 text-[#574BFB]" />
                    <div className="min-w-0 w-full">
                      <p className="text-[10px] text-muted-foreground">Entrega</p>
                      <p className="text-xs font-medium truncate">
                        {sale.delivery_method ? deliveryLabel : "—"}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#EAEFFE]/40 text-center">
                    <CreditCard className="h-4 w-4 text-[#574BFB]" />
                    <div className="min-w-0 w-full">
                      <p className="text-[10px] text-muted-foreground">Pagamento</p>
                      <p className="text-xs font-medium truncate">{paymentLabel}</p>
                    </div>
                  </div>
                </div>
              );
            })()}

            <Separator />

            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Cliente</p>
              {(sale.customer || sessionCustomer) ? (
                <div className="space-y-1">
                  <p className="font-medium">{displayCustomer.name || "Sem nome"}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {displayCustomer.instagram && (
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium">@</span>
                        <span>{displayCustomer.instagram.replace(/^@/, "")}</span>
                      </div>
                    )}
                    {displayCustomer.email && (
                      <div className="flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5" />
                        <span>{displayCustomer.email}</span>
                      </div>
                    )}
                    {displayCustomer.whatsapp && (
                      <div className="flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5" />
                        <span>{displayCustomer.whatsapp}</span>
                      </div>
                    )}
                  </div>
                  {displayCustomer.document && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <span>CPF/CNPJ: {displayCustomer.document}</span>
                    </div>
                  )}
                  {!sale.delivery_method && formattedCustomerAddress && (
                    <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{formattedCustomerAddress}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Venda sem cliente vinculado</p>
              )}
            </div>

            {sale.delivery_method && (
              <>
                <Separator />
                {(() => {
                  const shippingSvc = checkoutSession?.shipping_service as ShippingService | null | undefined;
                  const isPickup = sale.delivery_method === "pickup";
                  const isLocalDelivery = sale.delivery_method === "delivery" && shippingSvc?.company === "Loja";
                  const fallbackPickupStore = !pickupStore
                    ? storeAddresses?.find((address) => address.id === checkoutSession?.pickup_address_id)
                    : null;
                  const defaultPickupStore = !pickupStore && !fallbackPickupStore
                    ? storeAddresses?.[0]
                    : null;
                  const resolvedPickupStore = pickupStore || fallbackPickupStore || defaultPickupStore;

                  const SectionIcon = isPickup ? Store : isLocalDelivery ? Bike : Truck;
                  const sectionTitle = isPickup
                    ? "Retirada na Loja"
                    : isLocalDelivery
                      ? "Entrega Local"
                      : "Entrega via Transportadora";

                  const addr = shippingAddress?.street
                    ? shippingAddress
                    : displayCustomer.street
                      ? {
                          street: displayCustomer.street,
                          number: displayCustomer.number || "",
                          complement: displayCustomer.complement || undefined,
                          neighborhood: displayCustomer.neighborhood || "",
                          city: displayCustomer.city || "",
                          state: displayCustomer.state || "",
                          cep: displayCustomer.cep || "",
                        }
                      : null;

                  return (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground">Entrega</p>
                      <div className="rounded-lg border p-3 space-y-2.5">
                        <div className="flex items-center gap-2">
                          <SectionIcon className="h-4 w-4 text-[#574BFB]" />
                          <span className="text-sm font-medium">{sectionTitle}</span>
                          {!isPickup && shippingSvc?.name && (
                            <Badge variant="outline" className="text-xs ml-auto">
                              {shippingSvc.company !== "Loja" && `${shippingSvc.company} · `}{shippingSvc.name}
                            </Badge>
                          )}
                        </div>

                        {isPickup && resolvedPickupStore && (
                          <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span>
                              <span className="font-medium text-foreground">{resolvedPickupStore.name}</span>
                              {resolvedPickupStore.street && (
                                <>
                                  {" — "}
                                  {resolvedPickupStore.street}{resolvedPickupStore.number ? `, ${resolvedPickupStore.number}` : ""}
                                  {resolvedPickupStore.complement ? ` - ${resolvedPickupStore.complement}` : ""}
                                  {resolvedPickupStore.neighborhood ? `, ${resolvedPickupStore.neighborhood}` : ""}
                                  {resolvedPickupStore.city ? ` — ${resolvedPickupStore.city}` : ""}
                                  {resolvedPickupStore.state ? `/${resolvedPickupStore.state}` : ""}
                                  {resolvedPickupStore.cep ? ` · ${resolvedPickupStore.cep}` : ""}
                                </>
                              )}
                            </span>
                          </div>
                        )}

                        {isPickup && !resolvedPickupStore && (
                          <p className="text-xs text-muted-foreground">Cliente escolheu retirada na loja.</p>
                        )}

                        {!isPickup && addr && (
                          <div className="flex items-start gap-1.5 text-sm text-muted-foreground">
                            <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            <span>
                              {addr.street}{addr.number ? `, ${addr.number}` : ""}
                              {addr.complement ? ` - ${addr.complement}` : ""}
                              {addr.neighborhood ? `, ${addr.neighborhood}` : ""}
                              {addr.city ? ` — ${addr.city}` : ""}
                              {addr.state ? `/${addr.state}` : ""}
                              {addr.cep ? ` · ${addr.cep}` : ""}
                            </span>
                          </div>
                        )}

                        {!isPickup && !addr && (
                          <p className="text-xs text-muted-foreground">Endereço não informado</p>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {checkoutSession && (
              <>
                <Separator />
                <CheckoutLinkDisplay checkoutSession={checkoutSession} />
              </>
            )}

            {isDelivery && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground">Envio</p>
                    {!latestShipment && (
                      <Button
                        size="sm"
                        onClick={() => setShippingModalOpen(true)}
                      >
                        <Truck className="mr-2 h-4 w-4" />
                        Gerar Etiqueta
                      </Button>
                    )}
                  </div>
                  {latestShipment ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Serviço: </span>
                          <span className="font-medium">{latestShipment.service_name}</span>
                        </div>
                        {latestShipment.shipping_cost && (
                          <div>
                            <span className="text-muted-foreground">Custo: </span>
                            <span className="font-medium">{formatCurrency(latestShipment.shipping_cost)}</span>
                          </div>
                        )}
                      </div>
                      <ShippingStatus
                        status={latestShipment.status}
                        trackingCode={latestShipment.tracking_code}
                        labelUrl={latestShipment.label_url}
                        provider={latestShipment.provider}
                        shipmentId={latestShipment.id}
                        onCancel={(id) => {
                          if (!window.confirm("Cancelar esta etiqueta no SuperFrete? O valor será estornado ao seu saldo.")) return;
                          cancelShipmentMutation.mutate(id, {
                            onSuccess: () => {
                              toast({ title: "Envio cancelado", description: "A etiqueta foi cancelada no SuperFrete." });
                            },
                            onError: (err) => {
                              const message = err instanceof Error ? err.message : "Não foi possível cancelar a etiqueta.";
                              toast({ title: "Falha ao cancelar", description: message, variant: "destructive" });
                            },
                          });
                        }}
                        isCancelling={cancelShipmentMutation.isPending}
                        onRetryPrint={(id) => {
                          retryPrintMutation.mutate(id, {
                            onSuccess: () => {
                              toast({ title: "Etiqueta gerada!", description: "A etiqueta SuperFrete foi gerada com sucesso." });
                            },
                            onError: (err) => {
                              const message = err instanceof Error ? err.message : "Não foi possível gerar a etiqueta.";
                              toast({ title: "Falha ao gerar etiqueta", description: message, variant: "destructive" });
                            },
                          });
                        }}
                        isRetryingPrint={retryPrintMutation.isPending}
                        onViewLabel={(id) => {
                          fetchLabelMutation.mutate(id, {
                            onSuccess: (data) => {
                              const url = (data as { label_url?: string } | undefined)?.label_url;
                              if (url) {
                                window.open(url, "_blank");
                              } else {
                                toast({ title: "Etiqueta indisponível", description: "O SuperFrete não retornou a URL da etiqueta.", variant: "destructive" });
                              }
                            },
                            onError: (err) => {
                              const message = err instanceof Error ? err.message : "Não foi possível abrir a etiqueta.";
                              toast({ title: "Etiqueta indisponível", description: message, variant: "destructive" });
                            },
                          });
                        }}
                        isFetchingLabel={fetchLabelMutation.isPending}
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Nenhuma etiqueta gerada para esta venda.
                    </p>
                  )}
                </div>
              </>
            )}

            <Separator />

            <div className="space-y-3">
              <p className="text-xs font-medium text-muted-foreground">Produtos</p>
              {displayItems.length > 0 ? (
                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full text-sm table-fixed">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="px-2 sm:px-4 py-2.5 text-left font-medium text-muted-foreground">Produto</th>
                        <th className="px-1 sm:px-4 py-2.5 text-center font-medium text-muted-foreground w-10 sm:w-auto">Qtd</th>
                        <th className="px-1 sm:px-4 py-2.5 text-right font-medium text-muted-foreground w-16 sm:w-auto">Preço</th>
                        <th className="px-2 sm:px-4 py-2.5 text-right font-medium text-muted-foreground w-18 sm:w-auto">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayItems.map((item, index) => {
                        const product = products.find((p) => p.id === item.product_id);
                        const imageUrl = product?.image_urls?.[0];
                        const checkoutItems = (checkoutSession && Array.isArray(checkoutSession.items) ? checkoutSession.items.filter(Boolean) : []) as Array<Record<string, unknown>>;
                        const checkoutItem = checkoutItems.find((ci: any) =>
                          ci.product_id === item.product_id
                          && (item.variation_id ? ci.variation_id === item.variation_id : true)
                          && (item.variation_description ? ci.variation_description === item.variation_description : true)
                        ) || checkoutItems.find((ci: any) => ci.product_id === item.product_id);
                        const resolvedVariationId = item.variation_id
                          || checkoutItem?.variation_id
                          || (checkoutItem?.variation_description && product?.variations?.find((v: any) =>
                            (v.attributes || []).map((a: any) => a.attribute_value).join(" - ") === checkoutItem.variation_description
                          )?.id)
                          || (product?.has_variations && product.variations?.length === 1 ? product.variations[0].id : null)
                          || null;
                        const variation = resolvedVariationId ? product?.variations?.find((v) => v.id === resolvedVariationId) : null;
                        const variationLabel = item.variation_description || checkoutItem?.variation_description || variation?.attributes?.map((attr) => attr.attribute_value).join(" - ") || null;
                        const displayName = item.product_name || product?.name || "Produto";
                        const displayCode = item.product_code || product?.product_code || null;
                        const quantity = safeNumber(item.quantity, 1);
                        const unitPrice = safeNumber(item.unit_price, safeNumber(product?.sale_price, 0)) || safeNumber(item.total_price, 0) / Math.max(quantity, 1);
                        const totalPrice = safeNumber(item.total_price, unitPrice * quantity);

                        return (
                          <tr key={index} className="border-b last:border-0">
                            <td className="px-2 sm:px-4 py-3">
                              <div className="flex items-center gap-2 sm:gap-3">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={item.product_name}
                                    className="h-8 w-8 sm:h-10 sm:w-10 rounded object-cover shrink-0"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="h-8 w-8 sm:h-10 sm:w-10 rounded bg-muted flex items-center justify-center shrink-0">
                                    <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <p className="font-medium truncate text-xs sm:text-sm">{displayName}</p>
                                    {displayCode && (
                                      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                                        {displayCode}
                                      </span>
                                    )}
                                  </div>
                                  {variationLabel && (
                                    <p className="text-[10px] sm:text-xs text-muted-foreground truncate">
                                      {variationLabel}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-1 sm:px-4 py-3 text-center text-xs sm:text-sm">{quantity}</td>
                            <td className="px-1 sm:px-4 py-3 text-right text-muted-foreground text-xs sm:text-sm">
                              {formatCurrency(unitPrice)}
                            </td>
                            <td className="px-2 sm:px-4 py-3 text-right font-medium text-xs sm:text-sm">
                              {formatCurrency(totalPrice)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex items-center justify-center py-8 text-sm text-muted-foreground border rounded-lg">
                  <Package className="h-4 w-4 mr-2" />
                  Nenhum produto adicionado
                </div>
              )}
            </div>

            {showGenerateButton && (
              <CreateCheckoutButton
                saleId={sale.id}
                items={displayItems
                  .filter(item => item.product_id)
                  .map(item => {
                    const prod = products.find((p) => p.id === item.product_id);
                    const variation = prod?.variations?.find((v) => v.id === item.variation_id);
                    const quantity = safeNumber(item.quantity, 1);
                    const unitPrice = safeNumber(item.unit_price, safeNumber(prod?.sale_price, 0)) || safeNumber(item.total_price, 0) / Math.max(quantity, 1);
                    return {
                      product_id: item.product_id!,
                      product_name: item.product_name || prod?.name || "Produto",
                      product_code: item.product_code || prod?.product_code || null,
                      quantity,
                      unit_price: unitPrice,
                      variation_id: item.variation_id || undefined,
                      variation_description: item.variation_description || variation?.attributes?.map((attr) => attr.attribute_value).join(" - ") || undefined,
                      image_url: prod?.image_urls?.[0],
                    };
                  })}
                subtotal={Number(sale.subtotal)}
                discount={Number(sale.discount || 0)}
              />
            )}

            <div className="rounded-xl bg-[#EAEFFE]/20 border border-[#C2DBF6]/20 p-4 sm:p-5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal:</span>
                <span>{formatCurrency(Number(sale.subtotal))}</span>
              </div>

              {Number(sale.discount || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Desconto:</span>
                  <span className="text-destructive">- {formatCurrency(Number(sale.discount))}</span>
                </div>
              )}

              {checkoutSession?.shipping_cost && checkoutSession.shipping_cost > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Frete
                    {(checkoutSession.shipping_service as ShippingService | null)?.name && (
                      <span className="text-xs ml-1">
                        ({(checkoutSession.shipping_service as ShippingService).name})
                      </span>
                    )}
                  </span>
                  <span>{formatCurrency(Number(checkoutSession.shipping_cost))}</span>
                </div>
              )}

              <div className="border-t border-[#C2DBF6]/30 pt-2 mt-2">
                <div className="flex justify-between text-lg font-bold text-[#1E1446]">
                  <span>Total</span>
                  <span>{formatCurrency(Number(sale.total))}</span>
                </div>
              </div>
            </div>

            <Separator />

            <SaleTimeline events={events} isLoading={isHistoryLoading} />
            </>)}
          </div>

          <div className="shrink-0 border-t bg-[#EAEFFE]/10 px-4 sm:px-8 py-3 sm:py-4 flex justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              Fechar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => printSale(sale, products)}
            >
              <Printer className="h-4 w-4 mr-1.5" />
              Imprimir
            </Button>
          </div>
        </ResponsiveModalContent>
      </ResponsiveModal>

      <ShippingModal
        open={shippingModalOpen}
        onOpenChange={setShippingModalOpen}
        sale={sale}
        storeAddress={storeAddresses?.[0] || null}
        fallbackAddress={checkoutSession?.shipping_address as ShippingAddress | null | undefined}
        products={products}
      />
    </>
  );
}
